import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { slugify, unslugify } from "../utils/slugify";
import SEO from "../components/SEO";

const BASE_URL = process.env.REACT_APP_API_URL || "https://api.sofiasurgicals.com";

function TibialNail() {
  const location = useLocation();
  const { name } = useParams();

  const [product] = useState(location.state?.implant || null);
  const [selectedMaterial, setSelectedMaterial] = useState("Stainless Steel");
  const [showModal, setShowModal] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const stripHtml = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handleMaterialClick = (material) => setSelectedMaterial(material);
  const handleImageClick = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  // Fetch product data from API
  const getApiData = useCallback(async () => {
    setLoading(true);
    try {
      const implantName = product?.inplantsName || unslugify(name);

      const res = await axios.get(
        `${BASE_URL}/api/single-inplants-product-by-name/${implantName}`
      );
      setData(res.data?.data || null);
    } catch (error) {
      console.error("Error fetching product:", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [name, product?.inplantsName]);

  useEffect(() => {
    getApiData();
  }, [getApiData]);

  if (loading) {
    return (
      <>
        <SEO title="Loading Product..." url={`/implants-details/${name}`} />
        <p className="text-center mt-5 mb-5 fs-5">Loading product details...</p>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <SEO title="Product Not Found" url={`/implants-details/${name}`} />
        <p className="text-uppercase mb-5 mt-5 text-center fs-2">
          No product details available for this implant.
        </p>
      </>
    );
  }

  const {
    image,
    productDetails,
    stainlessDetails,
    titaniumDetails,
    seoTitle,
    metaDescription,
    metaKeywords,
    slug,
  } = data;

  const getImageUrl = (imagePath) =>
    imagePath ? `${BASE_URL}/${imagePath}` : "";

  const pageTitle = seoTitle || product?.inplantsName || unslugify(name) || "Product";
  const description = metaDescription || `High-quality ${pageTitle} manufactured by Sofia Surgicals India. ISO and CE certified.`;
  const keywords = stripHtml(metaKeywords || "");
  const imageUrl = getImageUrl(image);
  const canonicalUrl = `/implants-details/${slug || name || slugify(pageTitle)}`;

  return (
    <>
      <SEO
        title={pageTitle}
        description={description}
        keywords={keywords}
        image={imageUrl}
        url={canonicalUrl}
        type="product"
      />

      {image && productDetails ? (
        <div
          style={{
            background: "linear-gradient(175deg, #CEE5FD 0%, #FFFFFF 100%)",
          }}
          className="ThumbnailSection"
        >
          <div className="container">
            <div className="row px-3 px-md-5">
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 product-image-wrapper">
                <div className="product-image-container">
                  <img
                    src={imageUrl}
                    alt={pageTitle}
                    className="img-fluid thumbnail-image"
                    style={{
                      maxHeight: "350px",
                      width: "90%",
                      cursor: "pointer",
                      objectFit: "cover",
                    }}
                    onClick={handleImageClick}
                    loading="lazy"
                  />
                </div>
              </div>

              <div
                className="col-12 col-md-8"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(productDetails),
                }}
              />
            </div>

            {/* Modal Component */}
            <Modal show={showModal} onHide={handleCloseModal} centered>
              <Modal.Header closeButton>
                <Modal.Title>Product View</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <img src={imageUrl} alt={pageTitle} className="img-fluid" />
              </Modal.Body>
            </Modal>

            <div className="container pt-5">
              <div className="row px-3 px-md-5">
                <div
                  className="col-12 border border-primary rounded pt-2"
                  style={{ background: "#3496FF" }}
                >
                  <div
                    className="btn-group"
                    role="group"
                    aria-label="Basic radio toggle button group"
                  >
                    <input
                      type="radio"
                      className="btn-check"
                      name="btnradio"
                      id="btnradio1"
                      autoComplete="off"
                      value="Stainless Steel"
                      checked={selectedMaterial === "Stainless Steel"}
                      onChange={() => handleMaterialClick("Stainless Steel")}
                    />
                    <label
                      className="btn"
                      htmlFor="btnradio1"
                      style={{
                        background:
                          selectedMaterial === "Stainless Steel"
                            ? "#fff"
                            : "#3496FF",
                        color:
                          selectedMaterial === "Stainless Steel"
                            ? "#000"
                            : "#fff",
                        border: "none",
                        padding: "0.5rem 1rem",
                        borderRadius: "5px",
                      }}
                    >
                      Stainless Steel
                    </label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="btnradio"
                      id="btnradio2"
                      autoComplete="off"
                      value="Titanium"
                      checked={selectedMaterial === "Titanium"}
                      onChange={() => handleMaterialClick("Titanium")}
                    />
                    <label
                      className="btn"
                      htmlFor="btnradio2"
                      style={{
                        background:
                          selectedMaterial === "Titanium" ? "#fff" : "#3496FF",
                        color:
                          selectedMaterial === "Titanium" ? "#000" : "#fff",
                        border: "none",
                        padding: "0.5rem 1rem",
                        borderRadius: "5px",
                      }}
                    >
                      Titanium
                    </label>
                  </div>
                </div>
              </div>

              <div className="row mt-4 mb-4 px-3 px-md-5">
                <div className="col-12">
                  {selectedMaterial === "Stainless Steel" ? (
                    stainlessDetails ? (
                      <div
                        style={{ maxHeight: "400px", overflowY: "auto" }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(stainlessDetails),
                        }}
                      />
                    ) : (
                      <p>Stainless steel details are not available.</p>
                    )
                  ) : titaniumDetails ? (
                    <div
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(titaniumDetails),
                      }}
                    />
                  ) : (
                    <p>Titanium details are not available.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-center mt-5 mb-5 fs-5">No product details available for this implant.</p>
      )}
    </>
  );
}

export default TibialNail;